import {
    BLEND_NORMAL,
    CULLFACE_NONE,
    CapsuleGeometry,
    Color,
    Entity,
    Mesh,
    MeshInstance,
    StandardMaterial
} from 'playcanvas';

import { AvatarAnimationController } from './AvatarAnimationController';
import { AvatarModelLoader } from './AvatarModelLoader';
import { DEFAULT_AVATAR_MAPPING, createWalkAvatarState, type AvatarAnimationMapping } from './AvatarTypes';
import type { WalkController } from '../cameras/walk-controller';
import type { Global } from '../types';

const DEFAULT_YAW_OFFSET = 180;

/** 第三人称 Avatar 漫游功能，封装模型加载、动画切换和角色跟随。 */
class AvatarRoamingFeature {
    private _global: Global;

    private _walkController: WalkController;

    private _loader: AvatarModelLoader;

    private _entity: Entity | null = null;

    private _animation: AvatarAnimationController | null = null;

    private _walkState = createWalkAvatarState();

    private _mapping: AvatarAnimationMapping;

    private _loadToken = 0;

    private _wasVisible = false;

    private _enterModeFrameLock = 0;

    private _debug: boolean;

    private _debugFrame = 0;

    private _facingYaw = 0;

    private _hasFacing = false;

    /** 调试用半透明碰撞胶囊实体。 */
    private _debugCapsule: Entity | null = null;

    /** 是否显示调试胶囊（URL ?capsule 或 debug 模式开启）。 */
    private _showCapsule = false;

    constructor(global: Global, walkController: WalkController) {
        this._global = global;
        this._walkController = walkController;
        this._loader = new AvatarModelLoader(global.app);
        this._mapping = { ...DEFAULT_AVATAR_MAPPING, ...global.config.avatarMapping };
        this._debug = global.config.debug;
        this._showCapsule = this._debug || new URLSearchParams(location.search).has('capsule');

        this._walkController.thirdPersonEnabled = true;

        global.events.on('avatarEnabled:changed', (enabled: boolean) => {
            if (enabled) {
                this.load(global.state.avatarUrl || global.config.avatarUrl);
            } else {
                this._hide();
            }
            global.app.renderNextFrame = true;
        });

        global.events.on('avatarLoadUrl', (url: string) => {
            global.state.avatarUrl = url;
            if (global.state.avatarEnabled) {
                this.load(url);
            }
        });

        global.events.on('avatarMappingChanged', (mapping: AvatarAnimationMapping) => {
            this._mapping = { ...mapping };
            global.state.avatarMapping = { ...mapping };
            this._animation?.setMapping(mapping);
            localStorage.setItem('avatarMapping', JSON.stringify(mapping));
        });

        if (global.state.avatarEnabled && global.state.avatarUrl) {
            this.load(global.state.avatarUrl);
        }
    }

    /**
     * 加载指定 URL 的 Avatar GLB。
     *
     * @param url - GLB 资源地址。
     */
    async load(url: string): Promise<void> {
        const token = ++this._loadToken;
        const { state, app } = this._global;

        state.avatarLoading = true;
        state.avatarStatus = 'Loading avatar...';
        app.renderNextFrame = true;

        try {
            this.disposeModel();
            const { entity, animations } = await this._loader.load(url);
            if (token !== this._loadToken) {
                entity.destroy();
                return;
            }

            this._entity = entity;
            this._entity.enabled = state.avatarEnabled && state.cameraMode === 'avatar';
            try {
                this._animation = new AvatarAnimationController(entity, animations, this._mapping, this._debug);
                state.avatarAnimations = this._animation.animationNames;
                state.avatarCurrentAnimation = this._animation.current;
            } catch (animationErr) {
                const message = animationErr instanceof Error ? animationErr.message : String(animationErr);
                console.warn(`[AvatarRoamingFeature] 初始化 Avatar 动画失败，已降级为仅移动模型: ${message}`);
                this._animation = null;
                state.avatarAnimations = animations.map(asset => asset.name).filter(Boolean);
                state.avatarCurrentAnimation = '';
            }
            state.avatarAvailable = true;
            state.avatarStatus = `Avatar loaded (${animations.length} animations)`;
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            console.warn(message);
            state.avatarAvailable = false;
            state.avatarStatus = message;
        } finally {
            state.avatarLoading = false;
            app.renderNextFrame = true;
        }
    }

    /** 每帧更新 Avatar 位置、朝向和动画状态。 */
    update(): void {
        const { state, app } = this._global;
        const visible = state.avatarEnabled && state.cameraMode === 'avatar';

        if (!this._entity) {
            return;
        }

        this._entity.enabled = visible;
        if (!visible) {
            // 从可见切到不可见时主动补一帧，否则 render-on-demand 下画面会停留在带角色的旧帧，
            // 直到下次按键/相机移动才刷新，表现为“切换模式后人物没有立即消失”。
            if (this._wasVisible) {
                app.renderNextFrame = true;
            }
            this._wasVisible = false;
            this._enterModeFrameLock = 0;
            this._debugFrame = 0;
            this._hasFacing = false;
            if (this._debugCapsule) {
                this._debugCapsule.enabled = false;
            }
            return;
        }

        this._walkController.getAvatarState(this._walkState);
        this._entity.setPosition(this._walkState.footPosition);
        this._updateDebugCapsule();
        this._entity.setEulerAngles(0, this._computeFacingYaw(state.avatarFollowCamera), 0);
        if (!this._wasVisible) {
            this._animation?.play('idle');
            this._wasVisible = true;
            this._enterModeFrameLock = 8;
            this._log('进入 Avatar 模式', {
                grounded: this._walkState.grounded,
                velocity: {
                    x: this._walkState.velocity.x,
                    y: this._walkState.velocity.y,
                    z: this._walkState.velocity.z
                },
                moveMagnitude: this._walkState.moveMagnitude
            });
        }
        if (this._enterModeFrameLock > 0) {
            this._enterModeFrameLock -= 1;
            this._animation?.play('idle');
        } else {
            this._animation?.update(this._walkState);
        }
        this._debugFrame += 1;
        if (this._debug && this._debugFrame % 30 === 0) {
            this._log('每帧状态采样', {
                grounded: this._walkState.grounded,
                jumpStarted: this._walkState.jumpStarted,
                jumpHadInput: this._walkState.jumpHadInput,
                moveMagnitude: this._walkState.moveMagnitude,
                velocityY: this._walkState.velocity.y,
                currentAnimation: this._animation?.current ?? '(none)'
            });
        }
        state.avatarCurrentAnimation = this._animation?.current ?? '';
        app.renderNextFrame = true;
    }

    /** 释放当前 Avatar 模型。 */
    disposeModel(): void {
        if (this._entity) {
            this._entity.destroy();
            this._entity = null;
        }
        this._animation = null;
        this._loader.unload();
        this._global.state.avatarAnimations = [];
        this._global.state.avatarCurrentAnimation = '';
    }

    /** 释放模块管理的所有资源。 */
    destroy(): void {
        this.disposeModel();
        if (this._debugCapsule) {
            this._debugCapsule.destroy();
            this._debugCapsule = null;
        }
    }

    private _hide(): void {
        if (this._entity) {
            this._entity.enabled = false;
        }
    }

    /**
     * 懒创建调试用半透明碰撞胶囊，尺寸与 WalkController 胶囊参数一致。
     */
    private _createDebugCapsule(): void {
        if (this._debugCapsule) {
            return;
        }
        const wc = this._walkController;
        const radius = wc.capsuleRadius;
        // CapsuleGeometry 的 height 是端到端总高（内部会再减 2*radius 得到圆柱段），直接传胶囊总高
        const device = this._global.app.graphicsDevice;
        const geometry = new CapsuleGeometry({ radius, height: wc.capsuleHeight, sides: 16 });
        const mesh = Mesh.fromGeometry(device, geometry);

        const material = new StandardMaterial();
        material.diffuse = new Color(0.2, 0.7, 1);
        material.emissive = new Color(0.2, 0.7, 1);
        material.opacity = 0.3;
        material.blendType = BLEND_NORMAL;
        material.depthWrite = false;
        material.cull = CULLFACE_NONE;
        material.useLighting = false;
        material.update();

        const entity = new Entity('avatar-debug-capsule');
        entity.addComponent('render', {
            meshInstances: [new MeshInstance(mesh, material)],
            castShadows: false,
            receiveShadows: false
        });
        this._global.app.root.addChild(entity);
        this._debugCapsule = entity;
    }

    /** 每帧把调试胶囊定位到碰撞胶囊中心。 */
    private _updateDebugCapsule(): void {
        if (!this._showCapsule) {
            return;
        }
        if (!this._debugCapsule) {
            this._createDebugCapsule();
        }
        const wc = this._walkController;
        const foot = this._walkState.footPosition;
        // footPosition 已对齐到碰撞胶囊底部，胶囊中心 = 脚底 + 胶囊半高
        const centerY = foot.y + wc.capsuleHeight * 0.5;
        this._debugCapsule!.enabled = true;
        this._debugCapsule!.setPosition(foot.x, centerY, foot.z);
    }

    /**
     * 计算角色当前帧的朝向角（度），并做平滑过渡。
     * 跟随相机时面向相机前方；否则面向实际水平移动方向。
     *
     * @param followCamera - 是否跟随相机朝向。
     * @returns 平滑后的 Y 轴欧拉角（度）。
     */
    private _computeFacingYaw(followCamera: boolean): number {
        const { velocity } = this._walkState;
        const horizontalSpeed = Math.hypot(velocity.x, velocity.z);
        let targetYaw: number;

        if (followCamera) {
            // 跟随相机：面向相机前方（追尾视角）
            targetYaw = this._walkState.yaw + DEFAULT_YAW_OFFSET;
        } else if (horizontalSpeed > 0.05) {
            // 面向实际移动方向：PlayCanvas 实体 eulerY=θ 时前向为 (-sinθ,0,-cosθ)
            const deg = Math.atan2(-velocity.x, -velocity.z) * 180 / Math.PI;
            targetYaw = deg + DEFAULT_YAW_OFFSET;
        } else {
            // 静止时保持上一帧朝向
            targetYaw = this._hasFacing ? this._facingYaw : this._walkState.yaw + DEFAULT_YAW_OFFSET;
        }

        if (!this._hasFacing) {
            this._facingYaw = targetYaw;
            this._hasFacing = true;
            return this._facingYaw;
        }

        // 取最短路径角度差并按帧平滑插值
        let delta = targetYaw - this._facingYaw;
        while (delta > 180) delta -= 360;
        while (delta < -180) delta += 360;
        this._facingYaw += delta * 0.25;
        return this._facingYaw;
    }

    private _log(message: string, data?: object): void {
        if (!this._debug) {
            return;
        }
        if (data) {
            console.log(`[AvatarRoamingFeature] ${message}`, data);
        } else {
            console.log(`[AvatarRoamingFeature] ${message}`);
        }
    }
}

export { AvatarRoamingFeature };
